import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, MenuItem } from 'primeng/api';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { Contract } from '../contract.model';
import { ContractDetails } from '../contractDetails.model';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { FieldsetModule } from 'primeng/fieldset';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { forkJoin, switchMap } from 'rxjs';
import { ContractService } from '../contract.service';
import { TableModule } from 'primeng/table';
import { WorkflowApproval } from '../../chat-widgets/approval-card/workflow-apporval.model';
import { ApprovalCardComponent } from '../../chat-widgets/approval-card/approval-card.component';
import { AppUser } from '../../users/user.model';
import { Subscription } from 'rxjs';
import { ChatSession } from '../../models/chat-session.model';
import { ChatSessionService } from '../../services/chat-session.service';
import { WorkflowApprovalService } from '../../chat-widgets/approval-card/workflow-approval.service';
import { HttpParams } from '@angular/common/http';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { DropdownModule } from 'primeng/dropdown';
import { AuthenticationService } from '../../services/authentication.service';
import { TooltipModule } from 'primeng/tooltip';
import { AuthorizationService } from '../../services/authorization.service';
import { Supplier } from '../../master-data/suppliers/suppliers.model';
import { SuppliersService } from '../../master-data/suppliers/suppliers.service';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { Card } from 'primeng/card';
import { ContractAttachment } from '../contract-attachment.model';
import { ContractType } from '../contract-types';
import { DatePicker } from 'primeng/datepicker';
import { CalendarModule } from 'primeng/calendar';
import { TextareaModule } from 'primeng/textarea';
import { MAX_FILE_SIZE_BYTES } from '../../shared/constants/file-size.constants';
import { GENERAL_UPLOAD_ACCEPT_TYPES } from '../../shared/constants/upload-file-types.constants';
import { FirebaseStorageService } from '../../services/firebase-storage.service';
import { ProgressBarModule } from 'primeng/progressbar';
import { filter, map, take } from 'rxjs/operators';
import { AppDatePipe } from '../../pipes/app-date.pipe';

@Component({
  selector: 'app-view-contract',
  standalone: true,
  imports: [
    CommonModule,
    ToastModule,
    ProgressSpinnerModule,
    TagModule,
    FieldsetModule,
    BreadcrumbModule,
    ButtonModule,
    FileUploadModule,
    TableModule,
    ApprovalCardComponent,
    ChipModule,
    TooltipModule,
    DropdownModule,
    DialogModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    NgxExtendedPdfViewerModule,
    Card,
    DatePicker,
    CalendarModule,
    TextareaModule,
    ProgressBarModule,
    AppDatePipe,
  ],
  providers: [MessageService, CurrencyPipe],
  templateUrl: './contract-view.component.html',
  styleUrls: ['./contract-view.component.css'],
})
export class ContractViewComponent implements OnInit, OnDestroy {
  readonly maxFileSize = MAX_FILE_SIZE_BYTES;
  readonly attachmentAcceptTypes = GENERAL_UPLOAD_ACCEPT_TYPES;
  private sessionRouteSub!: Subscription;
  private contractRouteSub!: Subscription;
  private sessionDataSub!: Subscription;
  private authSubscription!: Subscription;
  currentSessionId: number | null = null;
  currentSession: ChatSession | undefined;

  currentUser: AppUser | null = null;
  contract: Contract | null = null;
  isLoading = false;
  isSaving = false;
  isEditMode = false;
  isDetailsEditMode = false;
  private detailsBackup: ContractDetails[] | null = null;
  headerForm!: FormGroup;
  headerFormUpdated!: FormGroup;

  contractId: number = 0;
  attachmentsChanged = false;
  isUploadingAttachments = false;
  isSavingAttachments = false;
  attachmentUploadProgress = 0;
  breadcrumbItems: MenuItem[] = [];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  approvals: WorkflowApproval[] = [];
  canUpdateBudgetaryControl: boolean = false;
  canUpdateFinance: boolean = false;
  displayApDialog = false;
  displayArDialog = false;
  pdfViewerVisible = false;
  pdfSrc: string | null = null;

  apDetailsForm!: FormGroup;
  arFinanceForm!: FormGroup;
  contractTypeId: number = 25;

  suppliers: Supplier[] = [];
  filteredSuppliers: Supplier[] = [];

  // Cache for storing fetched pre-signed URLs to avoid redundant API calls.
  private attachmentUrlCache = new Map<
    number,
    { url: string; expires: number }
  >();
  private attachmentProgressByIndex = new Map<number, number>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService,
    private authService: AuthenticationService,
    private authorizationService: AuthorizationService,
    private chatSessionService: ChatSessionService,
    private contractService: ContractService,
    private workflowApprovalService: WorkflowApprovalService,
    private fb: FormBuilder,
    private supplierService: SuppliersService,
    private storageService: FirebaseStorageService
  ) {}

  ngOnInit(): void {
    this.authSubscription = this.authService.currentUser$.subscribe((user) => {
      this.currentUser = user;
      if (user) {
        this.checkPermissions();
      }
    });

    // The traversal logic is confirmed to be working. We can remove the verbose logging.
    let routeWithSessionId: ActivatedRoute | null = this.route;
    while (
      routeWithSessionId &&
      !routeWithSessionId.snapshot.paramMap.has('sessionId')
    ) {
      routeWithSessionId = routeWithSessionId.parent;
    }

    this.sessionRouteSub = (
      routeWithSessionId || this.route
    ).paramMap.subscribe((params) => {
      const sessionIdParam = params.get('sessionId');

      if (sessionIdParam) {
        this.currentSessionId = +sessionIdParam;
        this.sessionDataSub?.unsubscribe();
        this.sessionDataSub = this.chatSessionService.sessions$.subscribe(
          (sessions) => {
            this.currentSession = sessions.find(
              (s) => s.sessionId === this.currentSessionId
            );
          }
        );
      } else {
        const errorMsg = 'Session ID not found in route parameters.';
        this.messageService.add({
          severity: 'error',
          summary: 'Routing Error',
          detail: errorMsg,
        });
        console.error(`[ViewContractComponent] ERROR: ${errorMsg}`);
      }
    });

    let routeWithPromoPlanId: ActivatedRoute | null = this.route;
    while (
      routeWithPromoPlanId &&
      !routeWithPromoPlanId.snapshot.paramMap.has('contractId')
    ) {
      routeWithPromoPlanId = routeWithPromoPlanId.parent;
    }

    this.contractRouteSub = (
      routeWithPromoPlanId || this.route
    ).paramMap.subscribe((params) => {
      const contractIdParam = params.get('contractId');

      if (contractIdParam) {
        this.contractId = +contractIdParam;
        this.loadContractData();
      } else {
        const errorMsg =
          'Contract ID not found in route parameters. Cannot load data.';
        this.messageService.add({
          severity: 'error',
          summary: 'Routing Error',
          detail: errorMsg,
        });
        console.error(`[ViewContractComponent] ERROR: ${errorMsg}`);
      }
    });

    this.initForms();
  }

  ngOnDestroy(): void {
    this.sessionRouteSub?.unsubscribe();
    this.contractRouteSub?.unsubscribe();
    this.sessionDataSub?.unsubscribe();
    this.authSubscription?.unsubscribe();
  }

  /**
   * Checks the current user's permissions and sets boolean flags
   * to be used in the template for showing/hiding/disabling UI elements.
   */
  private checkPermissions(): void {
    // Check for 'update' permission for the edit buttons.
    this.canUpdateBudgetaryControl = this.authorizationService.hasAnyRole(
      this.currentUser,
      ['Budgetary Control']
    );
    this.canUpdateFinance = this.authorizationService.hasAnyRole(
      this.currentUser,
      ['Finance AR']
    );
  }

  private initForms(): void {
    this.headerForm = this.fb.group({
      contractName: [''],
      startDateActive: [null],
      endDateActive: [null],
      contractNote1: [''],
    });

    this.apDetailsForm = this.fb.group({
      apVoucher: [''],
      apValue: [0],
      apVat: [0],
      apTotalAmount: [{ value: 0, disabled: true }],
      apArGlCodeCombination: [''],
      apApGlCodeCombination: [''],
      apTextReference: [''],
      serviceInvoice: [''],
      supplier: [null],
    });

    this.arFinanceForm = this.fb.group({
      arCmHash: [''],
    });
  }

  private loadContractData(): void {
    if (!this.contractId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No contract ID provided.',
      });
      return;
    }

    this.isLoading = true;
    this.contractService
      .getContractById(+this.contractId)
      .pipe(
        switchMap((contract) => {
          this.contract = contract;

          // حدد النوع الصحيح هنا
          this.contractTypeId =
            contract.contractType === ContractType.FIXED ? 25 : 26;

          // اعمل Request للـ approvals بعد تحديث الـ contractTypeId
          return this.workflowApprovalService.getAllHistory(
            new HttpParams({
              fromObject: {
                resourceTypeId: this.contractTypeId,
                businessObjectId: this.contractId,
              },
            })
          );
        })
      )
      .subscribe({
        next: (approvals) => {
          this.contract!.workflowApprovals = approvals.content;
        },
        error: () => {
          this.isLoading = false;
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  onStatusChange(status: WorkflowApproval) {
    this.contract!.contractStatus = status.workflowStatus;
    this.workflowApprovalService
      .getAllHistory(
        new HttpParams({
          fromObject: {
            resourceTypeId: this.contractTypeId, // Assuming 3 is the resource type for Contract
            businessObjectId: this.contractId!,
          },
        })
      )
      .subscribe({
        next: (approvals) => {
          this.contract!.workflowApprovals = approvals.content;
        },
        error: (err) => {
          console.error('Error fetching approvals:', err);
        },
      });
  }

  onFileSelect(event: any): void {
    if (!this.contract || !this.contract.contractId) {
      return;
    }

    const files: File[] = Array.isArray(event.files)
      ? event.files
      : Array.from(event.files ?? []);
    if (files.length === 0) {
      return;
    }

    if (!this.contract.attachments) {
      this.contract.attachments = [];
    }

    this.isUploadingAttachments = true;
    this.attachmentUploadProgress = 0;
    this.attachmentProgressByIndex.clear();
    const contractFolder = `contracts/${this.contract.contractId}`;

    forkJoin(
      files.map((file, index) => {
        const safeFileName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
        const storagePath = `${contractFolder}/${Date.now()}-${safeFileName}`;
        return this.storageService.uploadFileWithProgress(file, storagePath).pipe(
          filter(event => {
            this.updateAttachmentProgress(index, event.progress, files.length);
            return !!event.downloadURL;
          }),
          take(1),
          map(event => ({
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            fileUrl: event.downloadURL!,
          } as ContractAttachment))
        );
      })
    ).subscribe({
      next: (uploadedAttachments) => {
        this.contract!.attachments?.push(...uploadedAttachments);
        this.attachmentsChanged = true;
        this.isUploadingAttachments = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Files uploaded. Click save to attach them to the contract.',
        });
      },
      error: (err) => {
        this.isUploadingAttachments = false;
        this.attachmentUploadProgress = 0;
        console.error('Error uploading attachment to Firebase Storage:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Upload Failed',
          detail: 'Could not upload the selected file(s).',
        });
      },
    });
  }

  private updateAttachmentProgress(index: number, progress: number, totalFiles: number): void {
    this.attachmentProgressByIndex.set(index, progress);
    const totalProgress = Array.from(this.attachmentProgressByIndex.values()).reduce((sum, value) => sum + value, 0);
    this.attachmentUploadProgress = Math.round(totalProgress / Math.max(totalFiles, 1));
  }

  saveAttachments(): void {
    if (!this.contract || !this.contract.contractId || !this.contract.attachments) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No contract data available to save attachments.',
      });
      return;
    }

    const newAttachments = this.contract.attachments.filter(
      (att) => !att.attachmentId && !!att.fileUrl
    );

    if (newAttachments.length === 0) {
      this.attachmentsChanged = false;
      this.messageService.add({
        severity: 'info',
        summary: 'No Changes',
        detail: 'No new attachments to save.',
      });
      return;
    }

    this.isSavingAttachments = true;
    this.contractService.uploadAttachments(this.contract.contractId!, newAttachments).subscribe({
      next: (savedAttachments) => {
        this.isSavingAttachments = false;
        this.attachmentsChanged = false;
        const existingAttachments =
          this.contract!.attachments?.filter((att) => att.attachmentId) ?? [];
        this.contract!.attachments = [...existingAttachments, ...savedAttachments];
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Attachments saved successfully!',
        });
      },
      error: (err) => {
        this.isSavingAttachments = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save attachments.',
        });
        console.error('Error saving attachments:', err);
      },
    });
  }

  get details(): ContractDetails[] {
    return this.contract?.contractDetails || [];
  }

  /** A contract can only be edited while it is in Draft or Pending - Sales status. */
  get isEditable(): boolean {
    const status = this.contract?.contractStatus?.statusName;
    return status === 'Draft' || status === 'Pending - Sales';
  }

  // EditContractDetail(){

  // }

  editContract() {
     this.headerFormUpdated = this.headerForm;
    this.headerFormUpdated.patchValue({
      contractName: this.contract?.contractName ?? '',
      startDateActive: this.contract?.startDateActive ? new Date(this.contract.startDateActive) : null,
      endDateActive: this.contract?.endDateActive ? new Date(this.contract.endDateActive) : null,
      contractNote1: this.contract?.contractNote1 ?? '',
    });
    this.isEditMode = true;
  }

  
  formatDateToLocal(date: Date | null): string | null {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // JS months = 0-11
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  cancelEdit() {
    this.isEditMode = false;
  }

  saveContract() {
    if (!this.contractId) return;
    if (!this.isEditable) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Not Editable',
        detail: 'This contract can only be edited in Draft or Pending - Sales status.',
      });
      return;
    }
    this.isSaving = true;
    const { contractName, startDateActive, endDateActive, contractNote1 } = this.headerFormUpdated.value;
    this.contractService
      .updateContractHeader(this.contractId, {
        contractName,
         startDateActive: startDateActive ? this.formatDateToLocal(new Date(startDateActive)) : null,
         endDateActive: endDateActive ? this.formatDateToLocal(new Date(endDateActive)) : null,
        contractNote1,
      })
      .subscribe({
        next: (updated) => {
          this.contract = { ...this.contract!, ...updated };
          this.isEditMode = false;
          this.isSaving = false;
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Contract updated successfully.' });
        },
        error: () => {
          this.isSaving = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update contract.' });
        },
      });
  }
  editDetails() {
    if (!this.isEditable) return;
    // Snapshot the current details so a cancel can restore them.
    this.detailsBackup = JSON.parse(
      JSON.stringify(this.contract?.contractDetails ?? [])
    );
    this.isDetailsEditMode = true;
  }

  cancelEditDetails() {
    if (this.detailsBackup) {
      this.contract!.contractDetails = this.detailsBackup;
      this.detailsBackup = null;
    }
    this.isDetailsEditMode = false;
  }

  saveContractDetails() {
    if (!this.isEditable) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Not Editable',
        detail: 'This contract can only be edited in Draft or Pending - Sales status.',
      });
      return;
    }
    if (!this.contractId || !this.contract?.contractDetails?.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Details',
        detail: 'There are no contract details to update.',
      });
      return;
    }
    this.isSaving = true;
    this.contractService
      .updateAllContractDetails(this.contractId, this.contract.contractDetails)
      .subscribe({
        next: (updatedDetails) => {
          // Only replace the list when the API actually returns details,
          // otherwise an empty/void response would wipe the table (and the
          // toolbar that depends on details.length).
          if (Array.isArray(updatedDetails) && updatedDetails.length) {
            this.contract!.contractDetails = updatedDetails;
          }
          // Stay in edit mode so the user can keep saving; refresh the
          // backup snapshot so Cancel reverts to the just-saved state.
          this.detailsBackup = JSON.parse(
            JSON.stringify(this.contract!.contractDetails ?? [])
          );
          this.isSaving = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Saved',
            detail: 'Contract details updated successfully.',
          });
        },
        error: () => {
          this.isSaving = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update contract details.',
          });
        },
      });
  }

  goBackToList() {
    this.router.navigate([
      '/ai-studio/session',
      this.currentSessionId,
      { outlets: { canvasOutlet: ['rebate', 'list'] } },
    ]);
  }

  filterSuppliers(event: any) {
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredSuppliers = this.suppliers;
    } else {
      this.filteredSuppliers = this.suppliers.filter(
        (supplier) =>
          supplier.supplierName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          supplier.supplierNumber
            ?.toLowerCase()
            .includes(event.target.value.toLowerCase())
      );
    }
  }

  formatFileSize(bytes: number | undefined, decimals = 2): string {
    if (!bytes || bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  /**
   * A wrapper method that efficiently gets a secure URL (from cache or server)
   * and then performs the desired action (view, download, or email).
   */
  private _executeAttachmentAction(
    attachment: ContractAttachment,
    actionType: 'view' | 'download' | 'email',
    targetWindow?: Window | null
  ): void {
    // Action: DOWNLOAD on a LOCAL file (not yet saved).
    // This can be handled immediately without a server call.
    if (actionType === 'download' && attachment.fileContent) {
      this.downloadLocalAttachment(attachment, targetWindow);
      return;
    }

    if (attachment.fileUrl && !attachment.attachmentId) {
      this._performActionWithUrl(actionType, attachment, attachment.fileUrl, targetWindow);
      return;
    }

    // For all other actions on saved files, we need a secure URL.
    if (!this.contractId || !attachment.attachmentId) {
      const message =
        actionType === 'email'
          ? 'Please save new attachments before sharing them via email.'
          : 'This action is not available for unsaved attachments.';
      targetWindow?.close();
      this.messageService.add({
        severity: 'warn',
        summary: 'Action Unavailable',
        detail: message,
      });
      return;
    }

    // Check cache for a valid, non-expired URL.
    const cachedUrl = this.attachmentUrlCache.get(attachment.attachmentId);
    if (cachedUrl && cachedUrl.expires > Date.now()) {
      this._performActionWithUrl(actionType, attachment, cachedUrl.url, targetWindow);
      return;
    }

    // If not in cache or expired, fetch a new URL from the server.
    this.messageService.add({
      severity: 'info',
      summary: 'Loading',
      detail: 'Generating secure link...',
      life: 2000,
    });
    this.contractService
      .getAttachmentViewUrl(this.contractId, attachment.attachmentId)
      .subscribe({
        next: (response) => {
          if (response && response.url) {
            // Cache the new URL for 5 minutes.
            const expirationTime = Date.now() + 5 * 60 * 1000;
            this.attachmentUrlCache.set(attachment.attachmentId!, {
              url: response.url,
              expires: expirationTime,
            });
            this._performActionWithUrl(actionType, attachment, response.url, targetWindow);
          } else {
            targetWindow?.close();
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to retrieve a valid URL.',
            });
          }
        },
        error: (err) => {
          console.error(`Error getting attachment URL for ${actionType}:`, err);
          targetWindow?.close();
          this.messageService.add({
            severity: 'error',
            summary: 'Access Denied',
            detail: 'Could not get permission for the file.',
          });
        },
      });
  }

  /** Helper to execute the final action once a secure URL is obtained. */
  private _performActionWithUrl(
    actionType: 'view' | 'download' | 'email',
    attachment: ContractAttachment,
    url: string,
    targetWindow?: Window | null
  ): void {
    switch (actionType) {
      case 'view':
        if (attachment.fileType === 'application/pdf') {
          this.pdfSrc = url;
          this.pdfViewerVisible = true;
        } else {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
        break;
      case 'download':
        this.openUrlInAttachmentTab(url, targetWindow);
        break;
      case 'email':
        const subject = `Attachment: ${attachment.fileName} from Contract #${this.contract?.contractNo}`;
        let body = `Please find the attached file: ${attachment.fileName}.`;
        body += `\n\nYou can view or download it from this secure link: ${url}`;
        const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.location.href = mailtoLink;
        break;
    }
  }

  viewAttachment(attachment: ContractAttachment): void {
    this._executeAttachmentAction(attachment, 'view');
  }

  private openBlankAttachmentTab(): Window | null {
    const targetWindow = window.open('about:blank', '_blank');
    if (!targetWindow) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Popup Blocked',
        detail: 'Please allow popups to download attachments in a new tab.',
      });
    }
    return targetWindow;
  }

  private openUrlInAttachmentTab(url: string, targetWindow?: Window | null): void {
    if (targetWindow && !targetWindow.closed) {
      targetWindow.opener = null;
      targetWindow.location.href = url;
      return;
    }

    const openedWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (!openedWindow) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Popup Blocked',
        detail: 'Please allow popups to open attachments in a new tab.',
      });
    }
  }

  downloadAttachment(attachment: ContractAttachment): void {
    const targetWindow = this.openBlankAttachmentTab();
    if (!targetWindow) {
      return;
    }
    this._executeAttachmentAction(attachment, 'download', targetWindow);
  }

  emailAttachment(attachment: ContractAttachment): void {
    this._executeAttachmentAction(attachment, 'email');
  }

  /** Helper to download a new, unsaved file directly from its base64 content. */
  private downloadLocalAttachment(attachment: ContractAttachment, targetWindow?: Window | null): void {
    if (!attachment.fileContent) return;
    try {
      const base64Response = attachment.fileContent.split(',')[1];
      const byteCharacters = atob(base64Response);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: attachment.fileType });
      const objectUrl = window.URL.createObjectURL(blob);

      if (targetWindow && !targetWindow.closed) {
        targetWindow.opener = null;
        targetWindow.location.href = objectUrl;
        return;
      }

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = attachment.fileName;
      link.click();
    } catch (e) {
      console.error('Error decoding or downloading base64 file:', e);
      this.messageService.add({
        severity: 'error',
        summary: 'Download Failed',
        detail: 'Could not process the file for download.',
      });
    }
  }
}
